import "./styles/styles.scss";

import {
  FilterBar,
  FilterChip,
  FilterController,
  FilterList,
  FilterPopover,
  FilterSelection,
  FilterSelections,
} from "@amzn/stencil-react-components/filtering";
import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import { GridContainer, GridItem } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { ToggleSwitch } from "@amzn/stencil-react-components/toggle-switch";
import { IconFilterExtraSmall, IconSortExtraSmall } from "@amzn/stencil-react-icons";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { AiForMeItem } from "common/components/AIForMe/AiForMeItem";
import { CompareBar, CompareFlyout } from "common/components/AIForMe/Compare";
import { BuilderHubBanner } from "common/components/MessageBanners";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { CompareProvider, useCompare } from "common/context/compare";
import { useLists } from "common/context/lists/ListsContext";
import { fetchRecommendedToolsByJobFamily, TrackClickInteraction, trackUxmCustomEvent } from "common/helpers";
import { ONE_INDEX_BASED_RANK, SearchTelemetryMetadata } from "common/helpers/search-analytics";
import { useAiForMeFeedbacks, useTrackClickInteraction } from "common/hooks";
import { AiForMeURLQueryStrings } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config, PERMISSIONS } from "config";
import { useSearchExperience } from "hooks";
import { useAIForMeData } from "hooks/useAIForMeData";
import { AIForMeTool, sortingOptions, SortOption } from "lib/models/aiforme-models";
import { AmazonianInformation } from "lib/models/oui-models";
import { CatalogicalService, UserPermissions } from "lib/services";
import { GetItemsResponse } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import { CatalogicalSearchRequestContent } from "lib/services/shop-search-service";
import { UserInformation } from "lib/services/user-information-service";
import { debounce } from "lodash";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useAllAiTools } from "./useAllAiTools";

const CATEGORY_SOFT_DEV = "Software development";
const ALL_JOB_FAMILY_CATEGORY = "All";

export enum CategoriesWithTooltip {
  SOFTWARE_DEV = "Software development",
  LEGAL_MANAGEMENT = "Legal Management",
  LEGAL_PUBLIC_POLICY = "Legal & Public Policy",
}

const SEND_METRICS = true;

const HANDLER_DEBOUNCE_TIMEOUT = 300;
const PAGE_SIZE = 30;

const SORT_FILTER_OPTIONS = sortingOptions.map(({ label }) => ({ label, value: label }));

/**
 * Inner component that uses compare context
 */
const AllAiToolsContent: FC = () => {
  const { inputs, updateInputs } = useAllAiTools();
  const { categories, jobFamilies, sortingPriority, dataClassifications, loadingAIForMe } = useAIForMeData(true);
  const client = useMemo(() => new CatalogicalService<AIForMeTool>("aiforme"), []);
  const csrfService = useMemo(() => new CSRFService(), []);
  const location = useLocation();

  // Use the shared hook for feedbacks and vote handling
  const { userFeedbacks, voteHandler } = useAiForMeFeedbacks(client, csrfService);

  // Create the click tracking callback
  const trackClickInteraction: TrackClickInteraction = useTrackClickInteraction();
  // We know for sure the AmazonianInformation was obtained when AuthLoading component was executed
  const userInfo = UserInformation.current.getCachedAmazonianInformation() as AmazonianInformation;
  // Feature flag to enable semantic search
  const toggleAiForMeSemanticSearch = useMemo(
    () => UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AIFORME_SEMANTIC_SEARCH),
    []
  );
  // Feature flag to enable semantic search
  const toggleUseIntent = useMemo(() => UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_USE_INTENT), []);

  const { addErrorBanner } = useMessageBannersBox();
  const history = useHistory();
  const {
    items,
    isLoading,
    isLoadingMore,
    hasMoreItems,
    searchCatalogItems,
    searchNext,
    totalCount,
    nextToken,
    queryId,
  } = useSearchExperience<AIForMeTool>("aiforme");

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const { yourLists, getYourLists, evaluateLists } = useLists();
  const toggleLists = useMemo(() => UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS), []);

  // Calculate unique recommended tools not already in search results
  const uniqueRecommendedTools = useMemo(() => {
    if (!inputs.showRecommended || !inputs.recommendedTools || inputs.recommendedTools.size === 0) return [];

    const existingIds = new Set(items.map((item) => item.taxonomyId));
    const hasSelectedJobFamilies = inputs.selectedJobFamilies && inputs.selectedJobFamilies.length > 0;

    const filteredTools = hasSelectedJobFamilies
      ? inputs.selectedJobFamilies!.flatMap((jobFamily) => inputs.recommendedTools?.get(jobFamily) || [])
      : Array.from(inputs.recommendedTools.values()).flat();

    return Array.from(new Map(filteredTools.map((tool) => [tool.taxonomyId, tool])).values()).filter(
      (tool) => !existingIds.has(tool.taxonomyId)
    );
  }, [inputs.showRecommended, inputs.recommendedTools, inputs.selectedJobFamilies, items]);

  const totalTools = inputs.showRecommended ? totalCount + uniqueRecommendedTools.length : totalCount;

  // Check permissions on component mount
  useEffect(() => {
    document.title = getString("aiForMe.title");

    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (!UserPermissions.current.hasPermission(PERMISSIONS.AI_FOR_ME)) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, []);

  useEffect(() => {
    if (toggleLists) {
      getYourLists({
        allowedDomains: ["aiforme"],
      });
    }
  }, [toggleLists]);

  //  Call evaluate Lists Endpoint to retrieve Recommened Tools
  useEffect(() => {
    fetchRecommendedToolsByJobFamily<AIForMeTool>(jobFamilies, evaluateLists, ALL_JOB_FAMILY_CATEGORY).then((tools) =>
      updateInputs("recommendedTools", tools)
    );
  }, [jobFamilies, evaluateLists]);

  // Update banner visibility when categories change
  useEffect(() => {
    updateInputs(
      "showBuilderHubBanner",
      userInfo?.jobInfo.isTechnicalRole || (inputs.selectedCategories?.includes(CATEGORY_SOFT_DEV) ?? false)
    );
  }, [inputs.selectedCategories, userInfo?.jobInfo.isTechnicalRole, updateInputs]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useRef<HTMLDivElement | null>(null);

  const trackUxmGetItems = (params: {
    searchText?: string;
    filters: Record<string, string[] | number | undefined>;
    sortBy?: string;
    sortDirection?: string;
    pageSize: number;
    pageIndex: number;
  }) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      ...params,
    };

    trackCustomEvent(event, "get-ai-tools", trackerId);
  };

  const parseArrayParam = (param: string | null, validOptions: string[]) =>
    param?.split(",").filter((item) => validOptions.includes(item)) || [];

  const getURLParams = () => {
    const params = new URLSearchParams(location.search);

    const sortParam = params.get(AiForMeURLQueryStrings.SORT);
    const categoriesParam = params.get(AiForMeURLQueryStrings.CATEGORIES);
    const jobFamilyParam = params.get(AiForMeURLQueryStrings.JOB_FAMILY);
    const dataClassificationParam = params.get(AiForMeURLQueryStrings.DATA_CLASSIFICATION);
    const isUseCaseSearchParam = params.get(AiForMeURLQueryStrings.IS_USE_CASE_SEARCH);
    const showRecommended = params.get(AiForMeURLQueryStrings.SHOW_RECOMMENDED);
    const queryParam = params.get(AiForMeURLQueryStrings.QUERY);

    return {
      sortParam,
      categoriesParam,
      jobFamilyParam,
      dataClassificationParam,
      isUseCaseSearchParam,
      showRecommended,
      queryParam,
    };
  };

  // Listen for URL query param changes (from Header search)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get(AiForMeURLQueryStrings.QUERY) || undefined;
    if (queryParam !== inputs.searchText && inputs.isInitialized) {
      updateInputs("searchText", queryParam);
    }
  }, [location.search]);

  // Initialize filters and sorting from URL on component mount
  useEffect(() => {
    if (loadingAIForMe) return;

    const {
      sortParam,
      categoriesParam,
      jobFamilyParam,
      dataClassificationParam,
      isUseCaseSearchParam,
      showRecommended,
      queryParam,
    } = getURLParams();

    // Set search text from URL params
    if (queryParam) {
      updateInputs("searchText", queryParam);
    }

    // Set Job Families from URL params, if not set, then user's job family will be used
    if (jobFamilyParam) {
      const validJobFamiliesArray = parseArrayParam(jobFamilyParam, jobFamilies);
      updateInputs("selectedJobFamilies", validJobFamiliesArray);
    } else {
      updateInputs("selectedJobFamilies", []);
    }

    // Set data classification from URL params
    if (dataClassificationParam) {
      const validDataClassificationsArray = parseArrayParam(dataClassificationParam, dataClassifications);
      updateInputs("selectedDataClassification", validDataClassificationsArray);
    }

    // Set categories from URL params
    if (categoriesParam) {
      const validCategoriesArray = parseArrayParam(categoriesParam, categories);
      updateInputs("selectedCategories", validCategoriesArray);
    }

    if (showRecommended) {
      updateInputs("showRecommended", true);
    }

    // Set sortBy from URL params
    if (sortParam) {
      const newSort = sortingOptions.findIndex((option) => option.label === sortParam);

      if (newSort !== -1) {
        updateInputs("sortBy", sortingOptions[newSort]);
      }
    }

    if (isUseCaseSearchParam === "true") {
      updateInputs("isUseCaseSearch", true);
      // Use case filtering is only available with default sort option "Best Match"
      updateInputs("sortBy", sortingOptions[0]);
    }

    // Mark as initialized after URL params are processed
    updateInputs("isInitialized", true);
  }, [loadingAIForMe]);

  const updateUrl = useCallback(
    (newParams: Record<string, any>) => {
      const newUrlParams = new URLSearchParams();
      if (newParams.sortBy) {
        newUrlParams.set(AiForMeURLQueryStrings.SORT, newParams.sortBy);
      }

      if (newParams.searchText) {
        newUrlParams.set(AiForMeURLQueryStrings.QUERY, newParams.searchText);
      }

      if (newParams.selectedJobFamilies) {
        newUrlParams.set(AiForMeURLQueryStrings.JOB_FAMILY, newParams.selectedJobFamilies);
      }

      if (newParams.isUseCaseSearch) {
        newUrlParams.set(AiForMeURLQueryStrings.IS_USE_CASE_SEARCH, String(newParams.isUseCaseSearch));
      }

      if (newParams.selectedCategories && newParams.selectedCategories.length > 0) {
        newUrlParams.set(AiForMeURLQueryStrings.CATEGORIES, newParams.selectedCategories);
      }

      if (newParams.selectedDataClassification && newParams.selectedDataClassification.length > 0) {
        newUrlParams.set(AiForMeURLQueryStrings.DATA_CLASSIFICATION, newParams.selectedDataClassification);
      }

      if (newParams.showRecommended) {
        newUrlParams.set(AiForMeURLQueryStrings.SHOW_RECOMMENDED, String(newParams.showRecommended));
      }

      const newUrl = `${location.pathname}?${newUrlParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
    },
    [location.pathname, location.search]
  );

  const debouncedUpdateUrl = useMemo(
    () => debounce((params: Record<string, any>) => updateUrl(params), HANDLER_DEBOUNCE_TIMEOUT),
    [updateUrl]
  );

  const handleSortChange = useCallback((selection: FilterSelection) => {
    trackUxmCustomEvent("sort-tools", { selected: selection });
    if (selection) {
      const newSort = sortingOptions.findIndex((option) => option.label === (selection as string));
      updateInputs("sortBy", sortingOptions[newSort]);
      updateInputs("isUseCaseSearch", undefined);
    } else {
      updateInputs("sortBy", sortingOptions[0]);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  async function doGetItems(loadMore: boolean = false, currentRequestId?: number): Promise<void> {
    const useSemanticSearch = toggleAiForMeSemanticSearch || inputs.isUseCaseSearch;

    const filters: Record<string, any> = {
      status: ["APPROVED", "UPDATED"],
      ...(inputs.selectedCategories?.length && {
        categories: inputs.selectedCategories,
      }),
      ...(inputs.selectedJobFamilies?.length && {
        jobFamilies: [...inputs.selectedJobFamilies, ALL_JOB_FAMILY_CATEGORY],
      }),
      ...(inputs.selectedDataClassification?.length && {
        dataClassification: inputs.selectedDataClassification,
      }),
      ...(inputs.showRecommended && {
        sortingPriority: 1,
      }),
    };

    const currentPageIndex = nextToken / PAGE_SIZE;

    try {
      console.info(`Querying page ${currentPageIndex}`);
      trackUxmGetItems({
        searchText: inputs.searchText,
        filters,
        sortBy: inputs.sortBy?.field.join(","),
        sortDirection: inputs.sortBy?.order?.join(","),
        pageSize: PAGE_SIZE,
        pageIndex: currentPageIndex,
      });

      const nextRequest: CatalogicalSearchRequestContent = {
        searchQuery: inputs.searchText,
        useSemanticSearch,
        useIntent: toggleUseIntent,
        caseInsensitive: true,
        sortBy: inputs.sortBy?.field.join(","),
        sortDirection: inputs.sortBy?.order?.join(","),
        filter: JSON.stringify(filters),
        limit: PAGE_SIZE,
        offset: loadMore && hasMoreItems ? nextToken : 0,
        vectorField: "embeddings", // TODO: Move to Search service domain specific config
        excludeFields: "embeddings,embeddingsA",
        mode: inputs.isUseCaseSearch ? "usecase" : undefined,
      };

      let response: GetItemsResponse<AIForMeTool> | undefined;
      if (loadMore && hasMoreItems) {
        response = await searchNext(nextRequest);
      } else {
        response = await searchCatalogItems(nextRequest, SEND_METRICS);
      }

      // Ignore stale responses from previous requests
      if (currentRequestId !== undefined && currentRequestId !== requestIdRef.current) {
        return;
      }

      /* istanbul ignore next */
      if (!response) {
        const err = { name: "No response", message: "No response getting the items" };
        updateInputs("error", err);
        return;
      }

      // Good response, set data and pagination
      updateInputs("error", undefined);
    } catch (
      err
      /* istanbul ignore next */
    ) {
      updateInputs("error", err as Error);
    }
  }

  // Function to load more data when scrolling to the bottom
  /* istanbul ignore next */
  const loadMoreItems = useCallback(() => {
    if (!isLoading && !isLoadingMore && hasMoreItems) {
      console.info("Loading next page...");
      doGetItems(true);
    }
  }, [isLoading, isLoadingMore, hasMoreItems]);

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    if (isLoading || isLoadingMore) return;

    if (observer.current) observer.current.disconnect();

    /* istanbul ignore next */
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreItems) {
          loadMoreItems();
        }
      },
      { threshold: 0.5 }
    );

    if (lastElementRef.current) {
      observer.current.observe(lastElementRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [loadMoreItems, isLoading, isLoadingMore, hasMoreItems, items]);

  useEffect(() => {
    if (inputs.isInitialized) {
      debouncedUpdateUrl({
        searchText: inputs.searchText,
        selectedCategories: inputs.selectedCategories,
        selectedJobFamilies: inputs.selectedJobFamilies,
        selectedDataClassification: inputs.selectedDataClassification,
        sortBy: inputs.sortBy?.label,
        isUseCaseSearch: inputs.isUseCaseSearch,
        showRecommended: inputs.showRecommended,
      });

      // Debounce API calls to prevent multiple requests
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      const currentRequestId = ++requestIdRef.current;
      searchTimeoutRef.current = setTimeout(() => {
        doGetItems(false, currentRequestId);
      }, 100); // Short debounce to batch rapid state changes
    }
  }, [
    inputs.searchText,
    inputs.selectedCategories,
    inputs.selectedJobFamilies,
    inputs.selectedDataClassification,
    inputs.showRecommended,
    inputs.sortBy,
    inputs.isInitialized,
  ]);

  // Compare functionality
  const {
    addTool,
    removeTool,
    selectedToolIds,
    canAddMore,
    selectedTools,
    isFlyoutOpen,
    canCompare,
    openFlyout,
    closeFlyout,
    clearAll,
  } = useCompare();

  // Build search metadata for telemetry
  const getSearchTelemetry = (): SearchTelemetryMetadata => ({
    queryId: queryId ?? "-",
    resultCount: totalCount,
    visibleResultCount: items?.length ?? 0,
  });

  let children: React.JSX.Element[] | undefined;

  if (items) {
    children = items.map((item: AIForMeTool, index: number) => {
      // Get the user's vote for this item if it exists
      const userVote = userFeedbacks[item.taxonomyId];
      const isCompareSelected = selectedToolIds.has(item.taxonomyId);
      const rank = index + ONE_INDEX_BASED_RANK;

      return (
        <AiForMeItem
          key={item.taxonomyId}
          item={item}
          index={index}
          rank={rank}
          searchMetadata={getSearchTelemetry()}
          isRecommended={false}
          voteHandler={voteHandler}
          sortingPriorityTag={item.sortingPriority ? sortingPriority[item.sortingPriority] : undefined}
          client={client}
          userVote={userVote}
          lists={yourLists}
          gridSpan={{ lg: 4, md: 4 }}
          isCompareSelected={isCompareSelected}
          isCompareDisabled={!canAddMore && !isCompareSelected}
          onCompareAdd={addTool}
          onCompareRemove={removeTool}
          onTrackClick={trackClickInteraction}
        />
      );
    });

    if (inputs.showRecommended && uniqueRecommendedTools.length > 0) {
      const toolsLength = children?.length || 0;

      children = [
        ...(children || []),
        ...uniqueRecommendedTools.map((item: AIForMeTool, index: number) => {
          const userVote = inputs.userFeedbacks[item.taxonomyId];
          const isCompareSelected = selectedToolIds.has(item.taxonomyId);
          const rank = toolsLength + index + ONE_INDEX_BASED_RANK;

          return (
            <AiForMeItem
              key={item.taxonomyId}
              item={item}
              index={toolsLength + index}
              rank={rank}
              searchMetadata={getSearchTelemetry()}
              isRecommended={true}
              recommendedJobFamilies={inputs.selectedJobFamilies}
              voteHandler={voteHandler}
              sortingPriorityTag={item.sortingPriority ? sortingPriority[item.sortingPriority] : undefined}
              client={client}
              userVote={userVote}
              lists={yourLists}
              gridSpan={{ lg: 4, md: 4 }}
              isCompareSelected={isCompareSelected}
              isCompareDisabled={!canAddMore && !isCompareSelected}
              onCompareAdd={addTool}
              onCompareRemove={removeTool}
              onTrackClick={trackClickInteraction}
            />
          );
        }),
      ];
    }
  }

  const handleJobFamiliesFilterChange = useCallback((selection: any) => {
    updateInputs("selectedJobFamilies", selection as string[]);
    trackUxmCustomEvent("filter-job-family", { selected: selection });
  }, []);

  const handleDataClassificationFilterChange = useCallback((selection: any) => {
    updateInputs("selectedDataClassification", selection as string[]);
    trackUxmCustomEvent("filter-data-classification", { selected: selection });
  }, []);

  const handleCategoriesFilterChange = useCallback((selection: any) => {
    updateInputs("selectedCategories", selection as string[]);
    trackUxmCustomEvent("filter-categories", { selected: selection });
  }, []);

  const handleShowRecommendedChange = useCallback(() => {
    trackUxmCustomEvent("toggle-show-recommended", { value: !inputs.showRecommended });
    updateInputs("showRecommended", !inputs.showRecommended);
  }, [inputs.showRecommended]);

  const setFilterSelection = useCallback((values: FilterSelections) => {
    updateInputs("selectedJobFamilies", (values.jobFamily as string[]) || []);
    updateInputs("selectedCategories", (values.category as string[]) || []);
    updateInputs("selectedDataClassification", (values.dataClassification as string[]) || []);
  }, []);

  return (
    <PageMainContainer>
      <Spacer height={"S500"} />
      <Row alignItems="center">
        <Text fontSize="T400" fontWeight="bold" data-testid="results-header">
          {isLoading ? "" : totalTools} {getString("aiForMe.aiTools")}
        </Text>
        <Spacer width="S300" />
        <Text>{getString("aiForMe.recommendedTools")}</Text>
        <Spacer width="S200" />
        <ToggleSwitch checked={inputs.showRecommended} onClick={handleShowRecommendedChange} />
      </Row>
      <Spacer height={"S300"} />
      <Row justifyContent="space-between">
        <Row alignItems="center" height="40px" padding="S100" gridGap="S100">
          <IconFilterExtraSmall color="neutral90" />
          <Text style={{ whiteSpace: "nowrap" }}>{getString("aiForMe.filterBy")}</Text>
        </Row>
        <Spacer width="S200" />
        <FilterBar flyoutTitleText="Ai Tools Filters" onApply={setFilterSelection}>
          <FilterController name="jobFamily" data-cy="job-family-filter" titleText="Job family">
            <FilterList
              isMulti
              isSearchable
              titleText="Job family"
              options={jobFamilies.map((jobFamily) => ({ label: jobFamily, value: jobFamily }))}
              onFilterChange={handleJobFamiliesFilterChange}
              value={inputs.selectedJobFamilies}
            />
          </FilterController>
          <FilterController
            name="dataClassification"
            data-cy="data-clasification-filter"
            titleText="Data classification"
          >
            <FilterList
              isMulti
              isSearchable
              titleText="Data classification"
              options={dataClassifications.map((dataClassification) => ({
                label: dataClassification,
                value: dataClassification,
              }))}
              onFilterChange={handleDataClassificationFilterChange}
              value={inputs.selectedDataClassification}
            />
          </FilterController>
          <FilterController name="category" titleText="Categories" data-cy="category-filter">
            <FilterList
              isMulti
              isSearchable
              titleText="Categories"
              options={categories.map((category) => ({ label: category, value: category }))}
              onFilterChange={handleCategoriesFilterChange}
              value={inputs.selectedCategories}
            />
          </FilterController>
        </FilterBar>

        <Row alignItems="center" height="S600" padding="S100" gridGap="S100">
          <IconSortExtraSmall color="neutral90" />
          <Text style={{ whiteSpace: "nowrap" }}>{getString("aiForMe.sortBy")}</Text>
          <Spacer width="S200" />
          <FilterPopover
            triggerComponent={<FilterChip>{getString("aiForMe.sortBy")}</FilterChip>}
            data-cy="sort-by-filter"
          >
            <FilterList
              value={inputs.sortBy?.label}
              titleText={getString("aiForMe.sortBy")}
              options={SORT_FILTER_OPTIONS}
              onFilterChange={handleSortChange}
            />
          </FilterPopover>
        </Row>
      </Row>
      <Spacer height={"S300"} />

      {/* BuilderHub Banner */}

      {inputs.showBuilderHubBanner && (
        <>
          <div className="builderhub-link">
            <BuilderHubBanner />
          </div>
          <Spacer height={"S300"} />
        </>
      )}

      {isLoading && <Spinner size={SpinnerSize.Medium} />}

      <GridContainer>
        {!isLoading && children && (
          <>
            {children}
            <GridItem lg={9} xl={9} md={8} sm={4}>
              <div ref={lastElementRef} style={{ height: "20px", width: "100%" }}></div>
              {isLoadingMore && (
                <Row justifyContent="center" padding={16}>
                  <Spinner size={SpinnerSize.Small} />
                </Row>
              )}
              {!hasMoreItems && items && items.length > 0 && (
                <Row justifyContent="center" padding={16}>
                  <Text fontSize="T200" color="gray">
                    {getString("aiForMe.noMoreItems")}
                  </Text>
                </Row>
              )}
            </GridItem>
          </>
        )}
      </GridContainer>

      {/* Compare Bar - sticky bottom banner */}
      <CompareBar
        selectedTools={selectedTools}
        canCompare={canCompare}
        isComparing={isFlyoutOpen}
        onRemoveTool={removeTool}
        onCancel={clearAll}
        onCompareNow={openFlyout}
      />

      {/* Compare Flyout - modal with comparison table */}
      <CompareFlyout isOpen={isFlyoutOpen} tools={selectedTools} onClose={closeFlyout} onRemoveTool={removeTool} />
    </PageMainContainer>
  );
};

/**
 * Main AllAiTools page component wrapped with CompareProvider
 */
export const AllAiTools: FC = () => {
  return (
    <CompareProvider>
      <AllAiToolsContent />
    </CompareProvider>
  );
};
